import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Stack,
  Title,
  Text,
  Card,
  SimpleGrid,
  Group,
  Badge,
  Skeleton,
  ThemeIcon,
} from '@mantine/core';
import {
  IconBookmarks,
  IconChevronRight,
  IconFileText,
} from '@tabler/icons-react';
import { usePublicKnowledgeBaseService } from '@actschurch/ccms-sdk';
import type { KBCategoryModelView } from '@actschurch/ccms-sdk';
import { AISearchBar } from '@/components/AISearchBar';
import { kbCategoryHref } from '@/utils/slug';
import { RichTextPreview } from '@/components/common/RichText';
import { VisibilityBadge } from '@/components/common/VisibilityBadge';

/**
 * KB landing page — shows categories as cards. Drilling into a category
 * opens <c>/kb/categories/:slug</c>, which is where the article cards live.
 * Keeping the landing page category-only stops the page from getting
 * unwieldy as the article count grows.
 *
 * Data path: the public KB surface enforces status + visibility + audience
 * server-side, so anonymous visitors get the Public-visibility content and
 * signed-in users additionally see anything matching their audience. There's
 * no flat "all categories" endpoint — we list visible KBs then fan out one
 * `listCategoriesForKnowledgeBase` per KB. The fan-out is bounded (a handful
 * of KBs per tenant) so a single in-flight query batches the lot.
 */
export default function KnowledgeBaseIndex() {
  const publicKb = usePublicKnowledgeBaseService();

  // Single React Query that owns the KB → categories → article-count fan-out
  // so the cards either all appear together or all stay in skeleton; avoids
  // the staggered-render flicker we'd get from one query-per-KB.
  const { data, isLoading } = useQuery({
    queryKey: ['public-kb', 'index'],
    queryFn: async () => {
      const kbs = await publicKb.listKnowledgeBases();
      const categoriesPerKb = await Promise.all(
        kbs.map((kb) => publicKb.listCategoriesForKnowledgeBase(kb.knowledgeBaseID)),
      );
      const categories: KBCategoryModelView[] = categoriesPerKb.flat();
      // Article counts per category — best-effort, parallel. The public
      // surface returns only articles the caller may see, so the displayed
      // count is the count *for this viewer*, not a global total.
      const counts = await Promise.all(
        categories.map((c) =>
          publicKb
            .listArticlesInCategory(c.kbCategoryID)
            .then((arts) => [c.kbCategoryID, arts.length] as const),
        ),
      );
      return { categories, counts: new Map(counts) };
    },
  });

  const visibleCategories = useMemo(() => {
    const list = data?.categories ?? [];
    return [...list].sort((a, b) => {
      const ao = a.orderNo ?? Number.MAX_SAFE_INTEGER;
      const bo = b.orderNo ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [data]);

  const articleCountByCategory = data?.counts ?? new Map<number, number>();
  const totalArticles = Array.from(articleCountByCategory.values()).reduce((s, n) => s + n, 0);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>Knowledge Base</Title>
        <Text c="dimmed">
          Practical how-to guides for setup teams, tech volunteers and ministry leaders.
          Pick a category to dive in.
        </Text>
      </Stack>

      <AISearchBar />

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={180} radius="lg" />
          ))}
        </SimpleGrid>
      ) : visibleCategories.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconFileText size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No categories yet — check back soon.</Text>
          </Stack>
        </Card>
      ) : (
        <>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {visibleCategories.length} categor{visibleCategories.length === 1 ? 'y' : 'ies'}
            {' · '}
            {totalArticles} article{totalArticles === 1 ? '' : 's'}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {visibleCategories.map((c) => {
              const count = articleCountByCategory.get(c.kbCategoryID) ?? 0;
              return (
                <Card
                  key={c.kbCategoryID}
                  component={Link}
                  to={kbCategoryHref(c)}
                  withBorder
                  padding="lg"
                  radius="lg"
                  shadow="sm"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                  }}
                >
                  <Stack gap="sm">
                    <Group justify="space-between" align="flex-start">
                      <ThemeIcon
                        size={44}
                        radius="md"
                        variant="light"
                        color={c.color ?? 'brand'}
                      >
                        <IconBookmarks size={22} />
                      </ThemeIcon>
                      <IconChevronRight size={18} color="var(--mantine-color-dimmed)" />
                    </Group>
                    <Stack gap={4}>
                      <Title order={4} lh={1.2}>
                        {c.name}
                      </Title>
                      {/* Category descriptions come from the rich-text editor
                          in the admin form, so they're stored as HTML strings.
                          Render the plain-text version so the card doesn't
                          show <p style="…"> tags. */}
                      <RichTextPreview
                        html={c.description}
                        size="sm"
                        c="dimmed"
                        lineClamp={2}
                      />
                    </Stack>
                    <Group gap="xs">
                      <Badge variant="light" color={c.color ?? 'brand'} radius="xl">
                        {count} article{count === 1 ? '' : 's'}
                      </Badge>
                      {/* Show Unlisted/Restricted; Public renders nothing. */}
                      <VisibilityBadge
                        visibilityName={c.visibilityName}
                        visibilityColor={c.visibilityColor}
                      />
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}
